<template>
  <v-container>
    <RequiresProfileEvent v-if="requiresProfile" class="mb-4" />

    <PageHeading
      title="Events"
      :actions="actions"
      actions-namespace="events"
      broadcast-actions
    />

    <ActiveEvents class="mt-5" :items="items" />

    <EventMetrics class="mt-10" />

    <DataTable class="mt-10" v-bind="table" :loading="loading" :items="items" />
  </v-container>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import RequiresProfileEvent from "~/components/pages/events/RequiresProfileEvent.vue";
import ActiveEvents from "~/components/pages/events/ActiveEvents.vue";
import EventMetrics from "~/components/analytics/EventsMetrics.vue";
import { PublishableStatus } from "~/layers/admin/models";
import StatusCmp from "~/layers/admin/components/PublishableStatus";
import { FilterOperator } from "~/layers/admin/private/data-table/Filters/types";
import Event from "~/models/dobby/Event";
import {
  EventSimpleDate,
  EventTicketsSold,
  EventAvailableTickets,
} from "#components";
import { useToast } from "vue-toastification";

export default defineComponent({
  components: { RequiresProfileEvent, ActiveEvents, EventMetrics },
  setup() {
    definePageMeta({
      rememberNav: "Events",
    });

    const { handle } = useNamespacedEvent("events");
    const toast = useToast();
    const profile = useTransaction<any>("event-profile");

    const { items, loading } = useListData<Event>({
      query: Event.select(["id", "handle", "name", "status", "created_at"])
        .include("image")
        .include("type", ["id", "name"])
        .include("tickets", ["id"])
        .include("ticketsSold")
        .include("occurrences", ["starts_at", "ends_at"]),
      actions: {
        namespace: "events",
        removeOne: "events:delete",
        removeMany: "events:delete-many",
      },
    });

    const sortedItems = computed(() => {
      return [...items.value].sort((a, b) => {
        // Sort by earliest start date, with creation date as tiebreaker
        const startsAtA = a.$getEarliestStartsAt();
        const startsAtB = b.$getEarliestStartsAt();

        // If both have start dates, sort by start date first
        if (startsAtA && startsAtB) {
          const startDateDiff = startsAtB.diff(startsAtA);
          if (startDateDiff !== 0) return startDateDiff;
          // If start dates are the same, sort by creation date
          if (a.created_at && b.created_at) {
            return a.created_at.diff(b.created_at);
          }
          return 0;
        }

        // If only one has a start date, prioritize the one with start date
        if (startsAtA && !startsAtB) return -1;
        if (!startsAtA && startsAtB) return 1;

        // If neither has start dates, sort by creation date
        if (a.created_at && b.created_at) {
          return a.created_at.diff(b.created_at);
        }
        return 0;
      });
    });

    const requiresProfile = computed(() => {
      return !profile.fetching.value && !profile.original.value.exists;
    });

    const duplicate = useResourceDuplicate<Event>();

    handle("event:url", (event: Event) => {
      if (event.web_url) {
        navigator.clipboard.writeText(event.web_url);
        toast.info("URL copied to clipboard");
      }
    });

    handle("event:duplicate", duplicate);

    return {
      items: sortedItems,
      loading,
      requiresProfile,
    };
  },
  data: () => ({
    actions: defineActions([
      {
        title: "More options",
        items: [
          {
            title: "Update event profile",
            key: "event:profile",
            to: { name: "settings-profiles-event" },
          },
        ],
      },
      {
        title: "Add event",
        key: "add:event",
        primary: true,
      },
    ]),
    table: defineTable({
      showSelect: true,
      searchable: true,
      applyFilters: true,
      mobile: true,
      avatar: true,
      configurable: true,
      actionsNamespace: "events",

      emptyIcon: "jam:calendar-f",
      emptyTitle: "No events",
      emptyDescription: "Your events will appear here.",

      itemRoute: (item: Event) => ({
        name: "events-handle",
        params: { handle: item.handle },
      }),

      views: [
        { id: "all", title: "All" },
        {
          id: "future",
          title: "Upcoming events",
          emptyTitle: "No upcoming events",
          emptyDescription: "Your upcoming events will appear here.",
          filters: tableFilter({
            startsAt: {
              type: "date",
              operator: FilterOperator.GreaterOrEqual,
              values: [now()],
            },
          }),
        },
        {
          id: "past",
          title: "Past events",
          emptyTitle: "No past events",
          emptyDescription: "Your past events will appear here.",
          filters: tableFilter({
            startsAt: {
              type: "date",
              operator: FilterOperator.Lesser,
              values: [now()],
            },
          }),
        },
      ],

      filters: {
        startsAt: "date",
        // total: "float",
        status: {
          type: "checkbox",
          items: PublishableStatus,
        },
      },

      columns: [
        {
          title: "Date",
          key: "startsAt",
          component: EventSimpleDate,
          width: "60px",
        },
        {
          title: "Event",
          key: "name",
          sortable: true,
          width: "200px",
          type: "title",
        },
        {
          title: "Status",
          key: "status",
          component: StatusCmp,
        },
        { title: "Sold", key: "sold", component: EventTicketsSold },
        {
          title: "Tickets",
          key: "tickets",
          component: EventAvailableTickets,
          display: "desktop",
        },
        { title: "Type", key: "type.name", display: "desktop" },
      ],
      actions: [
        {
          title: "Delete",
          key: "events:delete-many",
          color: "red",
          icon: "mdi:delete-sweep-outline",
        },
      ],
      itemActions: [
        {
          title: "Copy URL",
          key: "event:url",
          icon: "heroicons:link",
        },
        {
          title: "Duplicate",
          key: "event:duplicate",
          icon: "mdi:content-copy",
        },
        {
          title: "Delete",
          key: "events:delete",
          color: "red",
          icon: "mdi:delete-sweep-outline",
        },
      ],
    }),
  }),
});
</script>
